/**
 * Opportunity-type-scoped activity center — FE helpers.
 *
 * Mirrors the BE concept introduced in
 * ``api/utils/db_user.py::get_user_allowed_opp_types`` and
 * ``api/utils/util.py::get_root_opportunity_type_id``. Keeps the FE's
 * tab-visibility / send-routing rules in one place instead of being
 * scattered across components.
 *
 * Resolution rules (must match BE):
 *  - A user's *allowed* root opp_types come from their UserDepartment
 *    rows whose Department points at an OpportunityType. The root id
 *    is the chain head reached by walking parent_type_id.
 *  - An empty allowed set means "unscoped" — admin / legacy user with
 *    no department mapping. Treat as access to ALL roots.
 *  - A lead's *available* root opp_types come from its leadOpportunities.
 *    A lead with only is_active=false opps still surfaces its root
 *    types so the tab strip stays consistent with what's on screen.
 *  - The actual *visible tabs* = intersection(lead roots, user roots).
 *    Plus: unscoped users see every tab the lead has.
 *  - Default tab = root of the most-recent opportunity (the BE returns
 *    opportunities ordered desc by createdDate / updatedDate; the
 *    first one's root wins).
 */

/**
 * Resolve the ROOT opportunity_type id from an OpportunityType-shaped
 * object. Walks the ``parentType`` chain to its head. Accepts whatever
 * depth the GraphQL response carries — the query body asks for at least
 * 3 levels (leaf → parent → grandparent) which is enough for any
 * production tree today.
 *
 * Returns ``null`` when the input is falsy.
 *
 * Resolution:
 *   - No ``parentType`` linked AND ``parentTypeId`` is null/falsy
 *     ⇒ this node IS the root; return its id.
 *   - ``parentType`` object is present ⇒ recurse into it. Caps the
 *     walk at 5 hops as a cycle guard.
 *   - ``parentType`` missing but ``parentTypeId`` is non-null ⇒ we know
 *     a parent exists but the GraphQL body didn't fetch it deep enough.
 *     Return the parentTypeId so we still group by SOMETHING above the
 *     leaf (not perfectly the root, but better than mistaking a leaf
 *     for a root). Add a console.warn so the FE owner sees the gap.
 */
export const getRootOppTypeId = oppType => {
  if (!oppType || !oppType.id) return null;
  let current = oppType;
  // Walk capped at 5 hops as a cycle guard. IDs are coerced to Number
  // before return — GraphQL ID fields come back as strings on some
  // queries (e.g. ``ME.userDepartments``) and as Int on others
  // (``leadNew.leadOpportunities``). Mixing types breaks Set lookups.
  for (let i = 0; i < 5; i += 1) {
    if (current.parentType && current.parentType.id) {
      current = current.parentType;
    } else if (current.parentTypeId) {
      // The chain has more levels but the query body stopped here. Use
      // parentTypeId as a fallback root marker.
      // eslint-disable-next-line no-console
      console.warn(
        `[oppTypeScoping] OpportunityType id=${current.id} has parentTypeId=${current.parentTypeId} but parentType was not loaded. Widen the GraphQL query body.`
      );
      return Number(current.parentTypeId);
    } else {
      return Number(current.id);
    }
  }
  return Number(current.id);
};

/**
 * Resolve the ROOT opportunity_type NAME from an OpportunityType-shaped
 * object. Same walk semantics as ``getRootOppTypeId`` but returns the
 * display name of the root row. Used for tab labels.
 */
export const getRootOppTypeName = oppType => {
  if (!oppType || !oppType.id) return "";
  let current = oppType;
  for (let i = 0; i < 5; i += 1) {
    if (current.parentType && current.parentType.id) {
      current = current.parentType;
    } else {
      return current.name || "";
    }
  }
  return current.name || "";
};

/**
 * Set of ROOT opp_type ids the user is permitted to act on.
 *
 * Reads ``user.allowedOppTypeIds`` — the BE-resolved field backed by
 * ``api/utils/db_user.py::get_user_allowed_opp_types_for_location``.
 * That helper walks the 4-layer chain (UserDepartment →
 * CompanyRoleDepartment → Departments at this location → unscoped)
 * AND caches the result in Redis for 1h.
 *
 * No client-side fallback to ``userDepartments``: the BE owns the
 * permission logic. If ``allowedOppTypeIds`` is missing from the
 * query body, the helper returns an empty Set (treated as unscoped),
 * which is the safe default but indicates the GraphQL query needs
 * the field added.
 *
 * Empty Set ⇒ unscoped (treat as ALL roots).
 */
export const getUserAllowedRootOppTypeIds = user => {
  if (!user || !Array.isArray(user.allowedOppTypeIds)) return new Set();
  return new Set(user.allowedOppTypeIds.map(Number).filter(Boolean));
};

/**
 * True when the user has no opp-type permission constraints. Prefers
 * the BE-resolved ``isOppTypeUnscoped`` boolean when present (it's
 * the authoritative answer), falls back to a size-zero check on the
 * resolved set so callers that don't fetch the boolean still work.
 */
export const isUserUnscoped = user => {
  if (user && typeof user.isOppTypeUnscoped === "boolean") {
    return user.isOppTypeUnscoped;
  }
  return getUserAllowedRootOppTypeIds(user).size === 0;
};

/**
 * Look up a single opportunity by id against the lead's already-loaded
 * ``leadOpportunities`` — e.g. resolving a message's ``leadOpportunityId``
 * scalar to the full row (opportunityType chain, crmLink, etc).
 *
 * ``GET_MESSAGES_V2`` intentionally only returns the scalar
 * ``leadOpportunityId`` per message (see the query body comment) rather
 * than a nested ``leadOpportunity`` object — resolving the full row per
 * message would mean a DB join per row. The FE does the join client-side
 * against data it already has.
 *
 * Returns ``null`` when ``leadOpportunityId`` is falsy, when
 * ``leadOpportunities`` is missing/empty, or when no matching entry is
 * found (e.g. stale cache, deleted opp, merged-lead timing).
 *
 * @param {string|number} leadOpportunityId
 * @param {Array} leadOpportunities — ``lead.leadOpportunities``
 */
export const getLeadOpportunityById = (leadOpportunityId, leadOpportunities) => {
  if (!leadOpportunityId || !Array.isArray(leadOpportunities) || leadOpportunities.length === 0) {
    return null;
  }
  return leadOpportunities.find(o => String(o?.id) === String(leadOpportunityId)) || null;
};

/**
 * Resolve the ROOT opp_type id for a single opportunity id. Thin wrapper
 * around :func:`getLeadOpportunityById` + :func:`getRootOppTypeId` — see
 * :func:`getLeadOpportunityById` for the null-handling contract. Callers
 * should treat ``null`` as "don't show an opp-type badge" rather than
 * falling back to a generic icon.
 *
 * @param {string|number} leadOpportunityId
 * @param {Array} leadOpportunities — ``lead.leadOpportunities``
 */
export const getRootOppTypeIdForLeadOpportunityId = (leadOpportunityId, leadOpportunities) =>
  getRootOppTypeId(getLeadOpportunityById(leadOpportunityId, leadOpportunities)?.opportunityType);

/**
 * Set of root opp_type ids the lead actually has opportunities under.
 * Used to disable tabs for roots the lead has no opps in — even when
 * the user is permitted for that root.
 */
export const getLeadAvailableRootOppTypeIds = lead => {
  const opps = lead?.leadOpportunities;
  if (!Array.isArray(opps) || opps.length === 0) return new Set();
  const roots = opps.map(opp => getRootOppTypeId(opp?.opportunityType)).filter(Boolean);
  return new Set(roots);
};

/**
 * Compute the tab strip for a lead in the activity center.
 *
 * Returns an array of unique ``{ rootOppTypeId, rootOppTypeName }``,
 * one per tab the current user should see. Order matches the order
 * of opportunities on the lead (so the freshest opp's root appears
 * first — that's also the default tab).
 *
 * @param {object} lead — selectedLead with ``leadOpportunities``.
 * @param {object} user — currentUser with ``userDepartments``.
 */
export const getVisibleTabs = (lead, user) => {
  const opps = lead?.leadOpportunities;
  if (!Array.isArray(opps) || opps.length === 0) return [];

  const userAllowed = getUserAllowedRootOppTypeIds(user);
  const userIsUnscoped = userAllowed.size === 0;

  const seen = new Set();
  const tabs = opps.reduce((acc, opp) => {
    const oppType = opp?.opportunityType;
    if (!oppType) return acc;
    const rootId = getRootOppTypeId(oppType);
    if (!rootId || seen.has(rootId)) return acc;
    if (!userIsUnscoped && !userAllowed.has(rootId)) return acc;
    seen.add(rootId);
    // Use the root opp_type's actual name (walks parent chain).
    acc.push({
      rootOppTypeId: rootId,
      rootOppTypeName: getRootOppTypeName(oppType),
    });
    return acc;
  }, []);

  // Tab strip order is stable by root id — Sales (1) before Service (2) —
  // regardless of which root the freshest opp on the lead happens to be.
  // Default-tab selection is decoupled (``getDefaultTabId``).
  tabs.sort((a, b) => a.rootOppTypeId - b.rootOppTypeId);
  return tabs;
};

/**
 * Pick the default tab — root of the most-recent (first) opportunity
 * the current user can see. Returns ``null`` when no tabs are visible
 * (lead has no opps or user can't see any of them). Independent of the
 * tab strip's display order so a Service-only freshest opp still selects
 * the Service tab even though Sales renders to its left.
 */
export const getDefaultTabId = (lead, user) => {
  const opps = lead?.leadOpportunities;
  if (!Array.isArray(opps) || opps.length === 0) return null;
  const visible = getVisibleTabs(lead, user);
  if (visible.length === 0) return null;
  const visibleIds = new Set(visible.map(t => t.rootOppTypeId));
  // Walk opps in the order the BE returned them (desc by recency).
  for (let i = 0; i < opps.length; i += 1) {
    const rootId = getRootOppTypeId(opps[i]?.opportunityType);
    if (rootId && visibleIds.has(rootId)) return rootId;
  }
  return visible[0].rootOppTypeId;
};

/**
 * Predicate: does an opportunity belong to the active tab?
 * Used for client-side filtering of cards inside Opportunities section
 * when the BE returns the unfiltered list.
 */
export const oppMatchesTab = (opp, selectedOppTypeId) => {
  if (!selectedOppTypeId) return true;
  const rootId = getRootOppTypeId(opp?.opportunityType);
  return Number(rootId) === Number(selectedOppTypeId);
};

/**
 * Pick the root opp_type id of the most-recent opportunity on a lead
 * that the current user is permitted to act on.
 *
 * Used by the lead-list rendering to derive each row's "active root"
 * from data already in the ``getLeads`` payload — no extra BE field
 * needed. Returns ``null`` when:
 *   - The lead has no opportunities loaded, OR
 *   - The user has zero allowed roots (unscoped → caller falls
 *     through to legacy unfiltered behavior), OR
 *   - None of the lead's opps belong to a root the user can see.
 *
 * Resolution mirrors the BE: order opps by ``updatedDate`` desc,
 * walk each opportunity's ``opportunityType`` chain to the root,
 * return the first root that's in the user's allowed set.
 *
 * @param {object} lead — lead row with ``leadOpportunities``.
 * @param {object} user — currentUser with ``userDepartments``.
 */
export const getMostRecentPermittedRootOppTypeId = (lead, user) => {
  const opps = lead?.leadOpportunities;
  if (!Array.isArray(opps) || opps.length === 0) return null;
  const allowed = getUserAllowedRootOppTypeIds(user);
  // Empty ``allowed`` set ⇒ unscoped user (admin / legacy) ⇒ every
  // root is permitted. Don't return null in that case — pick the
  // root of the most-recent opp. Without this, unscoped users would
  // land on no default tab and the toolbar trigger would render the
  // fallback "All other" label instead of the actual root.
  const isUnscoped = allowed.size === 0;
  // Stable copy then sort desc by updatedDate (falls back to createdDate
  // for opps without updates). Same ordering the BE uses.
  const sorted = [...opps].sort((a, b) => {
    const ta = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
    const tb = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
    return tb - ta;
  });
  for (let i = 0; i < sorted.length; i += 1) {
    const rootId = getRootOppTypeId(sorted[i]?.opportunityType);
    if (rootId && (isUnscoped || allowed.has(rootId))) return rootId;
  }
  return null;
};

/**
 * Filter-aware variant of :func:`getMostRecentPermittedRootOppTypeId`.
 *
 * Use when the lead list itself has an opp-type filter applied
 * (``opportunityTypeFilters`` in getLeads). A returned lead may carry
 * opps in OTHER root types too — without this helper, the default tab
 * and the lead-row preview pick the most-recent opp across the entire
 * set and can land outside the filter, making the row look like it
 * belongs to a different tab than the one the user filtered for.
 *
 * Resolution:
 *   - ``filterRootIds`` empty/null → no filter active, fall through to
 *     the unscoped helper (legacy behavior).
 *   - Otherwise: most-recent opp on the lead whose root is in BOTH the
 *     filter set AND the user's allowed set. If no opp matches, fall
 *     through to the unscoped helper so we never return null when
 *     ``leadOpportunities`` is non-empty.
 *
 * @param {object} lead — lead row with ``leadOpportunities``.
 * @param {object} user — currentUser with ``userDepartments``.
 * @param {Iterable<number|string>|null|undefined} filterRootIds — root
 *   opp_type ids the list is filtered to (already walked to root; leaf
 *   ids should be normalized by the caller via ``getRootOppTypeId``).
 */
export const getMostRecentPermittedRootOppTypeIdInFilter = (lead, user, filterRootIds) => {
  const filter = new Set(
    [...(filterRootIds || [])]
      .map(v => (v == null ? null : Number(v)))
      .filter(v => v != null && !Number.isNaN(v))
  );
  if (filter.size === 0) return getMostRecentPermittedRootOppTypeId(lead, user);

  const opps = lead?.leadOpportunities;
  if (!Array.isArray(opps) || opps.length === 0) return null;
  const allowed = getUserAllowedRootOppTypeIds(user);
  const sorted = [...opps].sort((a, b) => {
    const ta = new Date(a?.updatedDate || a?.createdDate || 0).getTime();
    const tb = new Date(b?.updatedDate || b?.createdDate || 0).getTime();
    return tb - ta;
  });
  for (let i = 0; i < sorted.length; i += 1) {
    const rootId = getRootOppTypeId(sorted[i]?.opportunityType);
    if (rootId) {
      const rootNum = Number(rootId);
      // Must be in the filter AND (for scoped users) in the user's
      // permitted set. Unscoped users (empty allowed set) match every
      // root that's in the filter.
      const inFilter = filter.has(rootNum);
      const permitted = allowed.size === 0 || allowed.has(rootNum);
      if (inFilter && permitted) return rootNum;
    }
  }
  // No opp matches the filter on this lead — degrade to unscoped so
  // we don't render a tab-less / opp-less row.
  return getMostRecentPermittedRootOppTypeId(lead, user);
};
